import qs from 'qs';
import { APIService, type RequestBody, type RequestHeaders } from '../../api/apiService';
import type File from '../../interfaces/File';
import type {
  ClassifyFilesResponse,
  ConvertToPdfResponse,
  ChatDocumentPayload,
  CheckFilesOfflinePayload,
  CreateFileFromTemplatePayload,
  CreateFilePublicShareLinkRequest,
  CreateFilePublicShareLinkResponse,
  CreateFolderPayload,
  CreateTagPayload,
  DeleteChatDocumentMessagesPayload,
  DeleteFileAccessPayload,
  FileAccessOtpResponse,
  FileOffline,
  FolderContentStats,
  GetFileAccessesPayload,
  GetFileAccessesResponse,
  GetFilesPayload,
  GetFilesResponse,
  GetRecentFilesResponse,
  GetTagUsersResponse,
  KeepFilesOfflinePayload,
  MarkFilesAsReadResponse,
  MarkFilesAsUnreadResponse,
  MoveFilesResponse,
  OpenFileLocallyPayload,
  PublicFileShareLinkSettings,
  RevokeDenyFileAccessPayload,
  SellFilePayload,
  UpdateFilesAccessPayload,
} from '../../interfaces/FileApiService';
import type FileDetail from '../../interfaces/FileDetail';
import type FilePermissions from '../../interfaces/FilePermissions';
import type { FilePublicShareLinkItem } from '../../interfaces/FilePublicShareLinkItem';
import type FileVersion from '../../interfaces/FileVersion';
import type FileWithToken from '../../interfaces/FileWithToken';
import type Tag from '../../interfaces/Tag';
import { getTransferBaseUrl } from '../../lib/transferBaseUrl';

export class FileService extends APIService {
  /**
   * @desc Get files
   * @param payload
   * @param headers
   * @param options Optional AbortSignal for cancellation (e.g. CSV export pagination).
   * @return {Promise<* | undefined>}
   */
  getFiles(
    payload: GetFilesPayload,
    headers?: RequestHeaders,
    options?: { signal?: AbortSignal },
  ): Promise<GetFilesResponse> {
    return this.request('/myfiles/v2', 'POST', { ...payload }, headers, undefined, options);
  }

  /**
   * Recently opened files for the caller (`GET /myfiles/recent`).
   * Client clamps `limit` to 1..50; omit `limit` to use the server default (20).
   * `X-Folder-Password-Sessions` is attached by httpClient.
   */
  getRecentFiles(limit?: number): Promise<GetRecentFilesResponse> {
    const clamped = limit === undefined ? undefined : Math.min(50, Math.max(1, Math.trunc(limit)));
    const query = clamped === undefined ? '' : `?${qs.stringify({ limit: clamped })}`;
    // Undeployed / hanging upstream must not pin Recent on "Loading files…".
    return this.request(`/myfiles/recent${query}`, 'GET', undefined, undefined, undefined, {
      timeout: 12_000,
    });
  }

  /**
   * Retrieves specific files from the server based on the provided file IDs.
   *
   * @param fileIds - The unique identifiers of the files to retrieve.
   * @returns {Promise<GetFilesResponse>} A promise that resolves to the response of the specific files retrieval request.
   */
  getSpecificFiles(fileIds: number[]): Promise<GetFilesResponse> {
    return this.request('/myfiles/specific', 'POST', fileIds);
  }

  /**
   * Retrieves a file from the server based on the provided file ID.
   *
   * @param {string|number} fileId - The unique identifier for the file. Can be either a string or a number.
   * @return {Promise} A promise that resolves with the server's response containing the file data.
   */
  getFile(fileId: string | number): Promise<FileDetail> {
    return this.request(`/myfiles/${fileId}`, 'GET');
  }

  /**
   * Retrieves aggregated content statistics for a folder: number of files, number of
   * subfolders and total size (bytes) of all recursive descendants (trashed items excluded).
   *
   * @param {string} cufi - The unique identifier (cufi) of the folder.
   * @return {Promise<FolderContentStats>} A promise that resolves with the folder content stats.
   */
  getFolderContentStats(cufi: string): Promise<FolderContentStats> {
    return this.request(`/myfiles/stats/${cufi}`, 'GET');
  }

  /**
   * Retrieves information for a specific file based on the given file ID.
   * @deprecated
   * @param {string | number} fileId - The unique identifier for the file.
   * @return {Promise<Object>} A promise that resolves to an object containing file information.
   */
  getFileInfo(fileId: string | number): Promise<FilePermissions> {
    return this.request(`/file/info/${fileId}`, 'GET');
  }

  /**
   * Retrieves a one-time 6-digit code for opening a protected file in the Viewer.
   *
   * @param {number} fileUid - The unique identifier of the file for which the OTP is requested.
   * @return {Promise<FileAccessOtpResponse>} Six-digit OTP (no secret).
   */
  getFileOtp(fileUid: number): Promise<FileAccessOtpResponse> {
    return this.request(`/myfiles/otp/${fileUid}`, 'GET');
  }

  /**
   * Moves files to a specified parent directory.
   *
   * @param {string | number} parent - The ID or name of the parent directory where the files should be moved.
   * @param {string[]} fileIds - An array of file IDs to be moved to the specified parent directory.
   * @return {Promise} A promise that resolves with the server response after the files are moved.
   */
  moveFiles(parent: string | number | null, fileIds: string[]): Promise<MoveFilesResponse> {
    return this.request(`/myfiles/move${parent ? `?parent=${parent}` : ''}`, 'PUT', fileIds);
  }

  /**
   * Duplicates the specified files with a given prefix.
   *
   * @param {string} prefix - The prefix to be added to the duplicated file names.
   * @param {string[]} fileIds - An array of file IDs representing the files to be duplicated.
   * @return {Promise<Response>} A promise that resolves to the server response after duplicating the files.
   */
  duplicateFiles(fileIds: string[], prefix?: string): Promise<GetFilesResponse> {
    let url = '/myfiles/duplicate';
    if (prefix !== null) {
      url += `?prefix=${prefix}`;
    }

    return this.request(url, 'POST', fileIds);
  }

  /**
   * Extracts content from a PDF file identified by the unique file identifier (fileUid).
   *
   * @param {string} fileUid - The unique identifier of the PDF file to be extracted.
   * @param {RequestHeaders} [headers] - Optional HTTP headers to include in the request.
   * @return {Promise<Response>} A promise that resolves to the response of the extraction request.
   */
  getPdfExtraction(cufi: string, headers?: RequestHeaders): Promise<Response> {
    return this.request(
      `/api/PdfExtraction/${cufi}`,
      'GET',
      undefined,
      headers,
      import.meta.env.VITE_APP_PDF_PROCESSING_BASE_URL,
    );
  }

  /**
   * Fetches the extraction link for a PDF file using the provided file unique identifier.
   *
   * @param {number} fileUid - The unique identifier of the PDF file to extract the link for.
   * @param {RequestHeaders} [headers] - Optional headers to include in the HTTP request.
   * @return {Promise<Response>} A promise that resolves to the HTTP response containing the PDF extraction link.
   */
  getPdfExtractionLink(fileUid: string, headers?: RequestHeaders): Promise<any> {
    return this.request(
      `/api/pdfextractionlink/${fileUid}`,
      'GET',
      undefined,
      headers,
      import.meta.env.VITE_APP_PDF_PROCESSING_BASE_URL,
    );
  }

  /**
   * Retrieves the viewer response for a specific file identified by its unique ID.
   *
   * @param {string} token - The authentication token used to authorize the request.
   * @return {Promise<Response>} A promise that resolves to the response object containing the viewer information.
   */
  getFileToken(token: string): Promise<string> {
    return this.request(`/myfiles/btoken/${token}`, 'GET');
  }

  /**
   * @desc Get tags
   * @return {Promise<* | undefined>}
   */
  getTags(): Promise<Tag[]> {
    return this.request(`/myfiles/tags`, 'GET');
  }

  /**
   * Retrieves the details of a specific tag based on its unique identifier.
   *
   * @param {number} tagId - The unique identifier of the tag to retrieve.
   * @return {Promise<Tag>} A promise that resolves to the tag object containing the tag's details.
   */
  getTag(tagId: number): Promise<Tag> {
    return this.request(`/myfiles/tag/${tagId}`, 'GET');
  }

  /**
   * @desc Get tag users
   * @param tagId
   * @return {Promise<void>}
   */
  getTagUsers(tagId: number): Promise<GetTagUsersResponse> {
    return this.request(`/myfiles/tag/visibility/${tagId}`, 'GET');
  }

  /**
   * Creates a new file based on a specified template.
   *
   * @param {CreateFileFromTemplatePayload} payload - The data required to create the file from the template, including template details and file attributes.
   * @return {Promise<File>} A promise that resolves with the created file object.
   */
  createFileFromTemplate(payload: CreateFileFromTemplatePayload): Promise<File> {
    return this.request('/myfiles/newtemplate', 'POST', { ...payload });
  }

  /**
   * @desc Create a tag
   * @param payload
   * @return {Promise<* | undefined>}
   */
  createTag(payload: CreateTagPayload): Promise<Tag> {
    return this.request('/myfiles/tag', 'POST', { ...payload });
  }

  /**
   * Creates a shortcut for a file or resource specified by the given ID.
   *
   * @param {string | number} id - The identifier of the file or resource for which the shortcut needs to be created.
   * @param parentId
   * @return {Promise<Response>} A Promise that resolves to the response object of the shortcut creation request.
   */
  createShortcut(id: string | number, parentId?: string | number | undefined): Promise<File> {
    return this.request(`/myfiles/shortcut/${id}`, 'PUT', parentId?.toString());
  }

  /**
   * @desc Delete a tag
   * @param id
   * @return {Promise<* | undefined>}
   */
  deleteTag(id: number): Promise<void> {
    return this.request(`/myfiles/tag/${id}`, 'DELETE');
  }

  /**
   * @desc Update file tag
   * @param fileId
   * @param tagIds
   * @return {Promise<* | undefined>}
   */
  updateFileTags(fileId: string | number, tagIds: number[]): Promise<File> {
    return this.request(`/myfiles/tag/${fileId}`, 'PUT', tagIds);
  }

  /**
   * Updates the description of a file based on the provided file ID.
   *
   * @param fileUid
   * @param {string} description - The new description to assign to the file.
   * @return {Promise<Response>} A promise that resolves with the server response after updating the file's description.
   */
  updateFileDescription(fileUid: number, description: string): Promise<Response> {
    return this.request(`/myfiles/description/${fileUid}`, 'PUT', description);
  }

  /**
   * @desc Update favourite files
   * @param fileIds
   * @param value
   * @return {Promise<* | undefined>}
   */
  updateFavouriteFiles(fileIds: string[], value: boolean): Promise<void> {
    return this.request(`/myfiles/favorite?value=${value}`, 'PUT', fileIds);
  }

  /**
   * Updates the archived status of a list of files.
   *
   * @param {string[]} fileIds - An array of file IDs representing the files to update.
   * @param {boolean} value - A boolean indicating the archived status to set for the files.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  updateArchivedFiles(fileIds: string[], value: boolean): Promise<void> {
    return this.request(`/myfiles/archive?value=${value}`, 'PUT', fileIds);
  }

  /**
   * @desc Update deleted files
   * @param fileIds
   * @param value
   * @return {Promise<* | undefined>}
   */
  updateDeletedFiles(fileIds: string[], value: boolean): Promise<void> {
    return this.request(`/myfiles/delete?value=${value}`, 'PUT', fileIds);
  }

  /**
   * @desc Delete files from trash
   * @param fileIds
   * @return {Promise<* | undefined>}
   */
  deleteFiles(fileIds: string[]) {
    return this.requestRaw<void>(`/myfiles/delete`, 'DELETE', fileIds);
  }

  /**
   * @desc Create a folder
   * @param payload
   * @return {Promise<* | undefined>}
   */
  createFolder(payload: CreateFolderPayload): Promise<File> {
    return this.request('/myfiles/newfolder', 'POST', { ...payload });
  }

  /**
   * Renames a file with the given file ID to the specified name.
   *
   * @param {string | number} fileId - The unique identifier of the file to be renamed.
   * @param {string} name - The new name for the file.
   * @return {Promise<Response>} A promise that resolves with the response of the rename operation.
   */
  renameFile(fileId: string | number, name: string): Promise<Response> {
    return this.request(`/myfiles/rename/${fileId}`, 'PUT', { name });
  }

  /**
   * Retrieves the count of unread files.
   *
   * Sends a GET request to the endpoint responsible for counting unread files.
   *
   * @return {Promise<number>} A promise that resolves to the number of unread files.
   */
  getCountFilesUnread() {
    return this.request('/file/countinfilesunread', 'GET');
  }

  /**
   * Searches for files based on the provided criteria in the payload.
   *
   * @param {RequestBody} payload - The criteria used to search for the files.
   * @return {Promise<Response>} - A promise that resolves with the search results.
   */
  searchFiles(payload: RequestBody, options?: { signal?: AbortSignal }) {
    return this.request('/myfiles/search', 'POST', payload, undefined, undefined, options);
  }

  /**
   * Method to check if files exist for the given payload and optional parent ID.
   *
   * @param fileNames - An array of file names to check for existence.
   * @param {string | null} parentId - The optional parent ID to narrow the scope of the file existence check.
   * @return {Promise<Response>} A promise that resolves to the response of the existence check.
   */
  checkFilesExists(fileNames: string[], parentId?: number | null): Promise<File[]> {
    const stringifiesParams = qs.stringify({ parent: parentId }, { skipNulls: true });
    return this.request(
      `/myfiles/exists${stringifiesParams ? `?${stringifiesParams}` : ''}`,
      'POST',
      fileNames,
    );
  }

  /**
   * Retrieves the list of file accesses.
   *
   * @param {RequestBody} payload - The request body containing necessary parameters.
   * @return {Promise<any>} A promise that resolves with the file accesses data.
   */
  getFileAccesses(payload: GetFileAccessesPayload): Promise<GetFileAccessesResponse> {
    return this.request(`/file/listaccesses`, 'POST', { ...payload });
  }

  /**
   * Updates the access permissions for a specified file using the provided payload.
   *
   * @param {UpdateFileAccessPayload} payload - The data structure containing file access information that needs to be updated.
   * @return {Promise<Response>} A promise that resolves to the response of the update request.
   */
  updateFilesAccess(payload: UpdateFilesAccessPayload): Promise<Response> {
    return this.request(`/v3/file/access`, 'PUT', { ...payload });
  }

  /**
   * Deletes the access permissions to a specified file.
   *
   * @param {RequestBody} payload - The payload containing information about the file and the access permissions to be deleted.
   * @return {Promise<Response>} A promise that resolves to the response of the delete request.
   */
  deleteFileAccess(payload: DeleteFileAccessPayload): Promise<Response> {
    return this.request(`/file/access/v2`, 'DELETE', { ...payload });
  }

  /**
   * When
   *
   * @param {RequestBody} payload - The payload containing information about the file and the access permissions to be deleted.
   * @return {Promise<Response>} A promise that resolves to the response of the delete request.
   */
  revokeDeniedFileAccess(payload: RevokeDenyFileAccessPayload): Promise<Response> {
    return this.request(`/file/revokedenied`, 'PUT', { ...payload });
  }

  /**
   * When
   *
   * @param {RequestBody} payload - The payload containing information about the file and the access permissions to be deleted.
   * @return {Promise<Response>} A promise that resolves to the response of the delete request.
   */
  sellFile(payload: SellFilePayload) {
    return this.request(`/myfiles/sale`, 'POST', { ...payload });
  }

  /**
   * Retrieves the details of a file that is for sale.
   *
   * @param {string} id - The unique identifier of the file to retrieve.
   * @return {Promise<Response>} A promise that resolves to the response of the file retrieval request.
   */
  getSellFile(id: string) {
    return this.request(`/file/forsale/${id}`, 'GET');
  }

  /**
   * Retrieves the sales information for the current user.
   *
   * @return {Promise<Response>} A promise that resolves to the response of the sales information retrieval request.
   */
  getSalesInfo() {
    return this.request(`/dashboard/sales-info`, 'GET');
  }

  /**
   * Uploads an image for a file that is for sale.
   *
   * @param {string | number} fileId - The unique identifier of the file to which the image is being uploaded.
   * @param {FormData} formData - The form data containing the image file to be uploaded.
   * @return {Promise<Response>} A promise that resolves to the response of the image upload request.
   */
  uploadSalesImage(fileId: string | number, formData: FormData) {
    return this.request(
      `/transfer/upload/sale/preview/${fileId}`,
      'POST',
      formData,
      undefined,
      getTransferBaseUrl(),
    );
  }

  /**
   * Removes a file from the store.
   *
   * @param {string | number} fileId - The unique identifier of the file to be removed from the store.
   * @return {Promise<Response>} A promise that resolves to the response of the file removal request.
   */
  removeFileFromStore(fileId: string | number) {
    return this.request(`/file/forsale/remove/${fileId}`, 'DELETE');
  }

  /**
   * Classifies files based on the provided file IDs.
   *
   * @param {number[]} fileIds - An array of unique identifiers of the files to be classified.
   * @param {boolean} [force] - When true, files already classified are re-classified instead of skipped.
   * @return {Promise<Response>} A promise that resolves to the response of the classification request.
   */
  classify(fileIds: number[], force?: boolean): Promise<ClassifyFilesResponse> {
    return this.request(`/ai/classify`, 'POST', force === true ? { fileIds, force } : { fileIds });
  }

  /**
   * Retrieves the AI summary for a specific file.
   *
   * @param {number} fileId - The unique identifier of the file for which the AI summary is requested.
   * @param {boolean} [forceRegenerate] - Optional flag to force regeneration of the summary.
   * @return {Promise<Response>} A promise that resolves to the response of the AI summary retrieval request.
   */
  aiSummary(fileId: number, forceRegenerate?: boolean) {
    const queryParam = forceRegenerate ? '?forceRegenerate=true' : '';
    return this.request(`/ai/getSummary/${fileId}${queryParam}`, 'GET');
  }

  /**
   * Initiates a chat session with a document using AI.
   *
   * @param {ChatDocumentPayload} payload - The payload containing information about the document and the chat session.
   * @return {Promise<Response>} A promise that resolves to the response of the chat session initiation request.
   */
  aiDocumentChat(payload: ChatDocumentPayload) {
    return this.request(`/ai/chatWithDocument`, 'POST', { ...payload });
  }

  getAiDocumentChat(fileId: string | number, guid?: string) {
    const params = new URLSearchParams({ fileId: fileId.toString() });
    if (guid) {
      params.append('guidChat', guid);
    }
    return this.request(`/ai/chatWithDocument/?${params.toString()}`, 'GET');
  }

  /**
   * Deletes chat messages from a document chat session.
   *
   * @param {DeleteChatDocumentMessagesPayload} payload - The payload containing the message IDs to delete.
   * @return {Promise<Response>} A promise that resolves to the response of the delete request.
   */
  deleteChatDocumentMessages(payload: DeleteChatDocumentMessagesPayload): Promise<Response> {
    return this.request('/ai/chatWithDocument', 'DELETE', { ...payload });
  }

  /**
   * Retrieves the versions of a file based on the provided file ID.
   *
   * @param {string} fileId - The unique identifier of the file for which the versions are requested.
   * @return {Promise<Response>} A promise that resolves to the response of the versions retrieval request.
   */
  getFileVersions(fileId: number): Promise<FileVersion[]> {
    return this.request(`/myfiles/versions/${fileId}`, 'GET');
  }

  /**
   * Restores a specific version of a file based on the provided file ID and version ID.
   *
   * @param {number} fileId - The unique identifier of the file to restore.
   * @param {number} versionId - The unique identifier of the version to restore.
   * @return {Promise<Response>} A promise that resolves to the response of the restore request.
   */
  restoreFileVersion(fileId: number, versionId: number) {
    return this.request(`/myfiles/versions/restore/${fileId}/${versionId}`, 'GET');
  }

  /**
   * @desc Get viewer token
   * @param fileId
   * @param payload
   * @returns
   */
  getFileForViewer(
    fileId: number | string,
    payload: {
      vid?: number | string;
      type?: string;
      acToken?: string;
      thumb?: boolean;
      /**
       * Capability hint: the client can play videos via `<video src>` with HTTP Range, so for
       * video files the API may issue a token with `fileViewEncrypted=false` and a lifetime that
       * covers the playback. Ignored by API versions that do not support it.
       */
      stream?: boolean;
    },
  ): Promise<FileWithToken> {
    const stringifiesParams = qs.stringify(payload, { skipNulls: true });

    const url = `/myfiles/jwt/${fileId}?${stringifiesParams}`;

    return this.request(url, 'GET');
  }

  /**
   * Open the file locally
   *
   * @param {any} payload - The payload containing information about the file to open locally.
   * @param {RequestHeaders} [headers] - Optional headers to include in the HTTP request.
   * @return {Promise<Response>} A promise that resolves to the HTTP response containing the file locally.
   */
  openFileLocally(payload: OpenFileLocallyPayload, headers?: RequestHeaders): Promise<any> {
    return this.request(
      `?openFG=1`,
      'POST',
      { ...payload },
      headers,
      import.meta.env.VITE_APP_REMOTEGRANT_AGENT_BASE_URL,
    );
  }

  /**
   * Keep the files offline
   * @param payload
   * @param headers
   * @returns
   */
  keepFilesOffline(payload: KeepFilesOfflinePayload, headers?: RequestHeaders): Promise<any> {
    return this.request(
      `?keepOffline=1`,
      'POST',
      { ...payload },
      headers,
      import.meta.env.VITE_APP_REMOTEGRANT_AGENT_BASE_URL,
    );
  }

  /**
   * Check if the files are offline
   * @param payload
   * @param headers
   * @returns
   */
  checkFilesOffline(
    payload: CheckFilesOfflinePayload,
    headers?: RequestHeaders,
  ): Promise<FileOffline[]> {
    return this.request(
      `?checkOffline=1`,
      'POST',
      { ...payload },
      headers,
      import.meta.env.VITE_APP_REMOTEGRANT_AGENT_BASE_URL,
    );
  }

  // --- Public File Share Link (v3/file/access/public) ---

  /**
   * Gets the public file share link settings.
   * GET /api/v3/file/access/public/settings
   * @returns {Promise<PublicFileShareLinkSettings>} A promise that resolves to the public file share link settings.
   */
  getPublicFileShareLinkSettings(fileId: number): Promise<PublicFileShareLinkSettings> {
    return this.request(`/v3/file/access/public/settings/${fileId}`, 'GET');
  }

  /**
   * Creates a public file share link for a file or folder.
   * POST /api/v3/file/access/public
   */
  createPublicFileShareLink(
    payload: CreateFilePublicShareLinkRequest,
  ): Promise<CreateFilePublicShareLinkResponse> {
    return this.request('/v3/file/access/public', 'POST', { ...payload });
  }

  /**
   * Lists all public file share links for the current user.
   * GET /api/v3/file/access/public
   */
  getPublicFileShareLinks(includeRevoked: boolean = false): Promise<FilePublicShareLinkItem[]> {
    return this.request(`/v3/file/access/public?includeRevoked=${includeRevoked}`, 'GET');
  }

  /**
   * Gets the public file share link for a specific file, if any.
   * GET /api/v3/file/access/public/{fileId}
   * Returns null when no link exists (404).
   */
  getPublicFileShareLink(fileId: number): Promise<FilePublicShareLinkItem | null> {
    return this.request(`/v3/file/access/public/${fileId}`, 'GET');
  }

  /**
   * Deletes (revokes) the public file share link for a file or folder.
   * DELETE /api/v3/file/access/public/{fileId}
   */
  deletePublicFileShareLink(fileId: number): Promise<void> {
    return this.request(`/v3/file/access/public/${fileId}`, 'DELETE');
  }

  /**
   * Marks files as read.
   * POST /api/v3/file/markasread
   * @param fileIds - Array of file IDs to mark as read.
   * @returns {Promise<MarkFilesAsReadResponse>} A promise that resolves to the response of the mark as read request.
   */
  markFilesAsRead(fileIds: number[]): Promise<MarkFilesAsReadResponse> {
    return this.request(`/file/markasread`, 'POST', { fileIds });
  }

  /**
   * Marks files as unread.
   * POST /api/v3/file/markasunread
   * @param fileIds - Array of file IDs to mark as unread.
   * @returns {Promise<MarkFilesAsUnreadResponse>} A promise that resolves to the response of the mark as unread request.
   */
  markFilesAsUnread(fileIds: number[]): Promise<MarkFilesAsUnreadResponse> {
    return this.request(`/file/markasunread`, 'POST', { fileIds });
  }

  /**
   * Owner-only: queue antivirus rescan for the file's current version.
   * POST /api/myfiles/{id}/antivirus/rescan
   */
  requestAntivirusRescan(fileId: number): Promise<void> {
    return this.request(`/myfiles/${fileId}/antivirus/rescan`, 'POST');
  }

  /**
   * Convert a file to PDF.
   * POST /api/myfiles/{fileId}/convert-to-pdf
   * @param fileId - The ID of the file to convert.
   * @param destinationFolderId - The destination folder ID (or null for root).
   * @returns {Promise<{processId: string}>} A promise that resolves to the process ID.
   */
  convertToPdf(fileId: number, destinationFolderId: number | null): Promise<ConvertToPdfResponse> {
    return this.request<ConvertToPdfResponse>(`/myfiles/${fileId}/convert-to-pdf`, 'POST', {
      destinationFolderId,
    });
  }
}
